import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Pill, PillDot } from '../../../ui';
import {
    ActionsLigne, CelluleNom, FilterBar, LienIcone, Muted,
} from '../../../security/security.styled';
import { formatDate } from '../../../security/severity';
import { PaneTable, triAlpha } from '../paneTable';

/* ─── Origine de l'accès ───
   Le champ décisif de ce tableau : une affectation « User » est directe et se
   retire ligne à ligne, une affectation « Group » est héritée et se retire dans
   le groupe. Sur le parc, 3 907 affectations sont directes et 442 héritées. */
const TYPES = {
    User: { libelle: 'Directe', ton: 'neutral', icone: 'bx-user' },
    Group: { libelle: 'Par groupe', ton: 'accent', icone: 'bx-group' },
    ServicePrincipal: { libelle: 'Service', ton: 'rssi', icone: 'bx-cog' },
};

// Le portail n'accepte que l'identifiant d'un objet qu'il connaît : utilisateur
// ou groupe. Pour un ServicePrincipal, la fiche réclame l'appId de
// l'application, que la collecte ne rapporte pas — pas de lien plutôt qu'un
// lien mort.
const URL_PRINCIPAL = {
    User: (id) => `https://portal.azure.com/#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/${id}/hidePreviewBanner~/true`,
    Group: (id) => `https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/${id}/menuId/`,
};

// L'application est le nom par lequel on cherche une ligne ici — pas le groupe
// qui la porte.
const TRI_PAR_NOM = [{ id: 'resourceDisplayName', desc: false }];

const COLONNES = [
    {
        header: "Application",
        accessorKey: 'resourceDisplayName',
        sortingFn: triAlpha,
        cell: ({ getValue }) => (
            <CelluleNom>
                <span className="nom">{getValue() || <Muted>Sans nom</Muted>}</span>
            </CelluleNom>
        ),
    },
    {
        header: "Origine de l'accès",
        accessorKey: 'principalDisplayName',
        cell: ({ row }) => {
            const { principalType, principalId, principalDisplayName } = row.original;
            const type = TYPES[principalType];
            const url = principalId && URL_PRINCIPAL[principalType]
                ? URL_PRINCIPAL[principalType](principalId)
                : null;
            return (
                <CelluleNom>
                    <Pill $tone={type?.ton || 'neutral'}>
                        {type?.icone && <i className={`bx ${type.icone}`} />}
                        {type?.libelle || principalType || 'Inconnue'}
                    </Pill>
                    <span className="nom">{principalDisplayName || <Muted>—</Muted>}</span>
                    {url && (
                        <ActionsLigne>
                            <LienIcone
                                $mini
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Objet Entra ID de ${principalDisplayName}`}
                                aria-label="Ouvrir dans Entra ID"
                            >
                                <i className="bx bxl-microsoft" />
                            </LienIcone>
                        </ActionsLigne>
                    )}
                </CelluleNom>
            );
        },
    },
    {
        header: "Accordé le",
        accessorKey: 'createdDateTime',
        cell: ({ getValue }) => <Muted>{formatDate(getValue())}</Muted>,
    },
    {
        // L'affectation révoquée reste en base — c'est l'historique des accès —
        // et arrivait ici sans se distinguer d'un accès en cours. Sur la préprod,
        // 831 lignes sont révoquées et 1 439 comptes en portent au moins une.
        header: "État",
        accessorKey: 'isRevoked',
        cell: ({ row, getValue }) => (getValue() === true
            ? (
                <Pill $tone="neutral" title={row.original.revokedAt ? `Constatée révoquée le ${formatDate(row.original.revokedAt)}` : undefined}>
                    <i className="bx bx-x" />
                    Révoquée
                </Pill>
            )
            : (
                <Pill $tone="ok">
                    <PillDot />
                    Active
                </Pill>
            )),
    },
];

export function UserApplications() {
    const { isActiveApplication, currentApplication } = useSelector((state) => state.application);
    const [masquerRevoquees, setMasquerRevoquees] = useState(false);

    const donnees = isActiveApplication ? (currentApplication?.value ?? currentApplication) : [];
    const toutes = Array.isArray(donnees) ? donnees : [];

    const revoquees = toutes.filter((ligne) => ligne.isRevoked === true).length;
    const affichees = masquerRevoquees ? toutes.filter((ligne) => ligne.isRevoked !== true) : toutes;

    return (
        <PaneTable
            titre="Applications"
            colonnes={COLONNES}
            donnees={affichees}
            triInitial={TRI_PAR_NOM}
            placeholder="Application, groupe…"
            badges={revoquees > 0 && (
                <Pill $tone="neutral">
                    {revoquees} révoquée{revoquees > 1 ? 's' : ''}
                </Pill>
            )}
            outils={revoquees > 0 && (
                <FilterBar style={{ alignItems: 'center' }}>
                    <label htmlFor="apps-masquer-revoquees">
                        <input
                            id="apps-masquer-revoquees"
                            type="checkbox"
                            checked={masquerRevoquees}
                            onChange={(event) => setMasquerRevoquees(event.target.checked)}
                        />
                        Accès en cours seulement
                    </label>
                </FilterBar>
            )}
            vide={{
                icone: 'bx-grid-alt',
                titre: "Aucune application",
                texte: "Aucune application d'entreprise n'est affectée à ce compte, ni directement ni par un groupe.",
            }}
        />
    );
}
